import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';

import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { observer } from 'mobx-react';
import {
	BringgTimeline,
	TimeLineClusterItem,
	TimelineItem,
	TimelineOptions,
	TimelineItemClick,
	TimelinePublicApi,
	TimelineRowHoverEvent,
	TooltipRenderOptions
} from '@bringg-frontend/bringg-timeline';
import { getClusterStatusClass, taskStyles } from '@bringg-frontend/monitoring-shared';
import moment from 'moment';

import { RUNS_MONITORING_CLASS_NAME } from '../routes-monitoring/routes-monitoring';
import useRouteMonitoringStore from '../stores/helpers/use-route-monitoring-store';
import { useBringgTimelineScroll } from '../hooks/use-timeline-scroll';
import TimelineRowEmitter from './timeline-row-emitter';
import { timelineProvider } from './timeline-provider';
import { useHasFeatureFlag } from 'bringg-web/utils/feature-flags';
import TaskTooltip from '../../shared/components/tooltips/task/task-tooltip';

import styles from './tasks-timeline.module.scss';

const shouldTriggerItemTooltip = (item: TimelineItemClick, event: React.MouseEvent) => {
	const isCluster = !!(item as TimeLineClusterItem).items?.length;
	const ctrlKey = event.metaKey || event.ctrlKey;

	return isCluster || !ctrlKey || item.tooltipActive;
};

const TasksTimeline = () => {
	const allowTimelineCluster = useHasFeatureFlag('allow_timeline_cluster');
	const rowEmitter = useRef(new TimelineRowEmitter());
	const timelineContainerRef = useRef<HTMLDivElement>(null);
	const bringgTimelineApiRef = useRef<TimelinePublicApi>(null);
	const [multiSelect, setMultiSelect] = useState(false);
	const { routesMonitoringStore, routeMonitoringTimelineViewStore, selectedTasksStore } = useRouteMonitoringStore();
	const { selectedTasks, setSelectedTasks, clearSelectedTasks } = selectedTasksStore;

	const { rows, zoom, shouldZoom } = routeMonitoringTimelineViewStore;

	useEffect(() => {
		if (zoom && shouldZoom) {
			const { start, end } = zoom;

			bringgTimelineApiRef.current?.fitRange(start, end);
			routeMonitoringTimelineViewStore.setShouldZoom(false);
		}
	}, [zoom, shouldZoom, bringgTimelineApiRef]);

	const onTimelineScroll = useBringgTimelineScroll(bringgTimelineApiRef);

	const taskTooltipRender = useCallback(
		(timelineItems: TimelineItem[], tooltipRenderOptions?: TooltipRenderOptions) => {
			const items = Array.isArray(timelineItems) ? timelineItems : [timelineItems];
			const tasks = items
				.map(timelineItem => getRootEnv().dashboardSdk.sdk.v2.tasks.get(timelineItem.id as number))
				.sort(timelineProvider.sortByPriority);

			return (
				<TaskTooltip
					multiSelect={multiSelect}
					setMultiSelect={setMultiSelect}
					redrewTooltip={tooltipRenderOptions?.redrew}
					tasks={tasks}
					dataTestId={`tooltip-${items[0].rowId}-${items.map(({ id }) => id).join('-')}`}
				/>
			);
		},
		[multiSelect]
	);

	const rowOverHandler = (overEvent: TimelineRowHoverEvent) => {
		if (overEvent.rowId) {
			rowEmitter.current.emitOver(overEvent.rowId);
		} else {
			rowEmitter.current.emitOver(null);
		}
	};

	const onTimelineClick = useCallback(
		(e: React.MouseEvent) => {
			if (!(e.target as HTMLElement).closest('[data-clickable=true]')) {
				selectedTasks.length > 0 && clearSelectedTasks();
			}
		},
		[selectedTasks]
	);

	const onItemClicked = (item: TimelineItemClick, event: React.MouseEvent) => {
		const isCluster = !!(item as TimeLineClusterItem).items?.length;
		const ctrlKey = event.metaKey || event.ctrlKey;
		const isTooltipActive = item.tooltipActive;

		setMultiSelect(ctrlKey);

		if (isCluster) {
			const someTasksSelected = selectedTasks.length > 0;

			if (someTasksSelected && ctrlKey) return;

			if (isTooltipActive) {
				clearSelectedTasks();
			} else {
				const task = timelineProvider.getFirstTaskFromClusterItem(item as TimeLineClusterItem);
				setSelectedTasks(task, ctrlKey);
			}
		} else {
			if (isTooltipActive && ctrlKey) return;

			const task = getRootEnv().dashboardSdk.sdk.v2.tasks.get(item.id as number);
			setSelectedTasks(task, ctrlKey);
		}
	};

	const bringgTimelineOptions: TimelineOptions = useMemo(
		() => ({
			keepLayoutOnDom: true,
			syncScrollOnWheel: true,
			minAxisTime: moment(routesMonitoringStore.range.from).valueOf(),
			maxAxisTime: moment(routesMonitoringStore.range.to).valueOf(),
			onRowHover: rowOverHandler,
			onScroll: onTimelineScroll,
			rowClassName: styles.routeRow,
			cluster: allowTimelineCluster
				? {
						getClusterClassName: getClusterStatusClass
				  }
				: false,
			timeAxisOptions: {
				hideDaysValue: true
			}
		}),
		[onTimelineScroll, allowTimelineCluster, routesMonitoringStore.range]
	);

	return (
		<div className={styles.container}>
			<div
				className={styles.timeLineContainer}
				ref={timelineContainerRef}
				onMouseLeave={() => rowEmitter.current.emitOver(null)}
			>
				<BringgTimeline
					className={taskStyles.tasksTimeline}
					ref={bringgTimelineApiRef}
					rows={rows}
					options={bringgTimelineOptions}
					onClick={onTimelineClick}
					onItemClick={onItemClicked}
					selectedItemsIds={new Set(selectedTasks.map(({ id }) => id))}
					tooltipRenderer={taskTooltipRender}
					tooltipCriteria={shouldTriggerItemTooltip}
					tooltipContainerSelector={RUNS_MONITORING_CLASS_NAME}
				/>
			</div>
			<div className={styles.footer}></div>
		</div>
	);
};

export default observer(TasksTimeline);
